import { Circle, Square } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Input } from "@/components/ui/primitives"
import { UiRange } from "@/components/ui/ui-range"
import { cn } from "@/lib/utils"

export const SHAPE_KINDS = ["ellipse", "rect"] as const
export type ShapeKind = (typeof SHAPE_KINDS)[number]
export const DEFAULT_SHAPE_KIND: ShapeKind = SHAPE_KINDS[0]

const SHAPE_KIND_ICONS: Record<ShapeKind, typeof Square> = {
  ellipse: Circle,
  rect: Square,
}

type Props = {
  open: boolean
  shapeKind: ShapeKind
  color: string
  shapeWidth: number
  labels: {
    rect: string
    ellipse: string
    color: string
    weight: string
  }
  onShapeKindChange: (kind: ShapeKind) => void
  onColorChange: (color: string) => void
  onWidthChange: (width: number) => void
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}

/** Shape tool flyout: rect/ellipse + color + weight. */
export function SketchShapeFlyout({
  open,
  shapeKind,
  color,
  shapeWidth,
  labels,
  onShapeKindChange,
  onColorChange,
  onWidthChange,
  onClose,
  anchorRef,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [weightOpen, setWeightOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      setWeightOpen(false)
      return
    }
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open, onClose, anchorRef])

  if (!open) return null

  const kinds = SHAPE_KINDS.map((kind) => ({
    kind,
    label: labels[kind],
    icon: SHAPE_KIND_ICONS[kind],
  }))

  return (
    <div
      ref={panelRef}
      className="absolute top-full left-0 z-50 mt-1 flex flex-col gap-1 rounded-lg border border-ui-border bg-ui-bg-elevated p-1.5 shadow-xl"
    >
      {kinds.map((item) => {
        const Icon = item.icon
        const active = shapeKind === item.kind
        return (
          <button
            key={item.kind}
            type="button"
            title={item.label}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg text-ui-text-secondary outline-none hover:bg-ui-surface-raised hover:text-ui-text-primary",
              "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
              active
                ? "bg-ui-accent/15 text-ui-text-primary ring-1 ring-ui-accent ring-inset"
                : "ring-1 ring-transparent ring-inset",
            )}
            onClick={() => onShapeKindChange(item.kind)}
          >
            <Icon className="h-4 w-4" />
          </button>
        )
      })}

      <label
        className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg ring-1 ring-transparent outline-none ring-inset focus-within:ring-[var(--ui-focus-ring)] hover:bg-ui-surface-raised"
        title={labels.color}
      >
        <span
          aria-hidden
          className="h-5 w-5 rounded-full border border-black/25 shadow-sm dark:border-white/25"
          style={{ backgroundColor: color.startsWith("#") ? color.slice(0, 7) : "#111827" }}
        />
        <input
          type="color"
          value={color.startsWith("#") ? color.slice(0, 7) : "#111827"}
          className="absolute inset-0 cursor-pointer opacity-0"
          onChange={(e) => onColorChange(e.target.value)}
        />
      </label>

      <div className="relative">
        <button
          type="button"
          title={labels.weight}
          className={cn(
            "flex h-9 w-9 flex-col items-center justify-center gap-0.5 rounded-lg outline-none hover:bg-ui-surface-raised",
            "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
            weightOpen ? "bg-ui-accent/15 ring-1 ring-ui-accent ring-inset" : "ring-1 ring-transparent ring-inset",
          )}
          onClick={() => setWeightOpen((v) => !v)}
        >
          <span className="block h-0.5 w-4 rounded bg-ui-text-primary" />
          <span className="block h-1 w-4 rounded bg-ui-text-primary" />
          <span className="block h-1.5 w-4 rounded bg-ui-text-primary" />
        </button>

        {weightOpen ? (
          <div className="absolute top-0 left-full z-50 ml-2 w-56 rounded-lg border border-ui-border bg-ui-bg-elevated p-3 shadow-xl">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-ui-text-secondary">{labels.weight}</span>
              <div className="flex items-center gap-2">
                <UiRange
                  min={1}
                  max={16}
                  value={shapeWidth}
                  className="min-w-0 flex-1"
                  onChange={(e) => onWidthChange(Number(e.target.value))}
                />
                <Input
                  type="number"
                  min={1}
                  max={16}
                  value={shapeWidth}
                  className="h-8 w-14 shrink-0 px-1.5 text-center text-xs"
                  onChange={(e) => onWidthChange(Math.min(16, Math.max(1, Number(e.target.value) || 1)))}
                />
              </div>
            </label>
          </div>
        ) : null}
      </div>
    </div>
  )
}
